'use strict';
const observable = require('@mindmup/mapjs-core/src/util/observable'),
	googleMetadataToFileContext = require('./google-metadata-to-file-context');
module.exports = function GoogleDriveFilePageController(config, gapiScriptLoader, navigatorModel, authenticationModel, googleDriveService, formatConverter, startAuthenticator, googleFileEditingSession, initialContent, activityLog) {
	let fileLoadTimeoutId,
		importedFileId,
		userId,
		isLoaded;
	const self = observable(this),
		clearTimeout = function () {

			if (fileLoadTimeoutId) {
				navigatorModel.clearTimeout(fileLoadTimeoutId);
				fileLoadTimeoutId = 0;
			}
		},
		sendError = function (reason) {
			clearTimeout();
			if (activityLog) {
				activityLog.error(reason, 'google-page-controller');
			}
			self.dispatchEvent('error', reason);
		},
		startTimeout = function () {
			const timeout = config.timeout || 60000;
			clearTimeout();
			fileLoadTimeoutId = navigatorModel.setTimeout(function () {
				self.dispatchEvent('error', 'network-error');
			}, timeout);
		},
		lastPathComponent = function () {
			return (navigatorModel.getPath().indexOf(config.editorUrl) === 0) &&
				navigatorModel.getPath().length > config.editorUrl.length &&
				navigatorModel.getPath().replace(/.*\//, '');
		},
		getFileId = function (driveState) {
			if (driveState.action === 'open' && driveState.ids && driveState.ids[0]) {
				return driveState.ids[0];
			}
			return lastPathComponent();
		},
		loadFile = function (fileId) {
			if (activityLog) {
				activityLog.log('google-page-controller', 'loadFile', fileId);
			}
			self.dispatchEvent('fileLoading');
			return googleFileEditingSession.loadFile(fileId)
				.then((fileContext) => {
					if (activityLog) {
						activityLog.log('google-page-controller', 'loadFile:loaded', JSON.stringify(fileContext));
					}
					navigatorModel.replaceUrl(fileContext.title, config.editorUrl + '/' + fileContext.fileId);
					clearTimeout();
					self.dispatchEvent('fileLoaded', fileContext.fileId, fileContext.title, fileContext);
					if (fileContext.fileId !== fileId) {
						importedFileId = fileId;
						self.dispatchEvent('fileImported', fileContext.title, fileContext.fileId);
					}
					isLoaded = true;
					self.dispatchEvent('ready');
				})
				.catch((e) => sendError(e));
		},
		openFile = function (fileId) {
			if (activityLog) {
				activityLog.log('google-page-controller', 'openFile', fileId);
			}
			self.dispatchEvent('fileLoading');
			navigatorModel.replaceUrl('loading...', config.editorUrl + '/' + fileId); // required as the unsaved changes model works based on a URL
			return googleDriveService.getMetaData(fileId)
				.then(googleMetadataToFileContext)
				.then(fileContext => {
					if (activityLog) {
						activityLog.log('google-page-controller', 'openFile:fileContext', JSON.stringify(fileContext));
					}
					if (fileContext.isMigrated) {
						self.dispatchEvent('fileConfirmationRequired', fileContext);
					} else if (fileContext.canEdit) {
						return loadFile(fileId);
					} else if (fileContext.isRealtime) {
						sendError('realtime-unavailable');
					} else {
						self.openReadOnly(fileId);
					}
				})
				.catch((e) => sendError(e));
		},
		getDriveState = function () {
			const queryStringParams = navigatorModel.getQueryStringAsMap();
			return (queryStringParams.state && JSON.parse(queryStringParams.state.replace(/,}$/, '}'))) || {};
		},
		createFile = function (folderId, theme) {
			if (activityLog) {
				activityLog.log('google-page-controller', 'createFile', 'folderId', folderId, 'theme', theme);
			}
			if (theme) {
				initialContent.attr = initialContent.attr || {};
				initialContent.attr.theme = theme;
			}
			return googleDriveService.createPlainFile(
				config.defaultFileTitle,
				folderId,
				JSON.stringify(initialContent)
			)
			.then(googleMeta => {
				if (activityLog) {
					activityLog.log('google-page-controller', 'createFile:meta', JSON.stringify(googleMeta));
				}
				return loadFile(googleMeta.id);
			})
			.catch(sendError);
		};

	if (formatConverter && formatConverter.addEventListener) {
		formatConverter.addEventListener('confirmationRequired', function () {
			clearTimeout();
			self.dispatchEvent('uploadConfirmationRequired');
		});
		formatConverter.addEventListener('uploadInProgress', function (arg) {
			self.dispatchEvent('uploadInProgress', arg);
		});
	}

	authenticationModel.addEventListener('authenticatedProfile', function (profile) {
		const driveState = getDriveState(),
			profileUserId = profile.providerProfile && profile.providerProfile.id;
		if (isLoaded) {
			if (userId !== profileUserId) {
				self.editFileId(getFileId(getDriveState()), profileUserId);
			} else {
				self.dispatchEvent('reauthenticated');
				return;
			}
		}

		userId = profileUserId;
		startTimeout();
		self.dispatchEvent('fileLoading');

		gapiScriptLoader.ensureEditorScripts()
		.then(() => {
			if (driveState.action === 'create') {
				createFile(driveState.folderId, navigatorModel.getQueryStringAsMap().theme);
			} else if (getFileId(driveState)) {
				openFile(getFileId(driveState));
			} else {
				sendError('invalidRequest');
			}
		})
		.catch(() => sendError('script-loading-failed'));
	});
	authenticationModel.addEventListener('authenticating', function () {
		clearTimeout();
		if (isLoaded) {
			self.dispatchEvent('reauthenticating');
		}
		self.dispatchEvent('authenticating');
	});
	authenticationModel.addEventListener('not-authenticated', function () {
		self.dispatchEvent('not-authenticated');
	});

	self.getLoginHint = function () {
		return userId || getDriveState().userId;
	};

	self.redirectToDrive = function () {
		const fileId = getFileId(getDriveState());
		navigatorModel.redirect(config.googleDriveUrlPrefix + fileId);
	};
	self.redirectToDriveAdmin = function () {
		navigatorModel.redirect(config.googleDriveAdminUrl);
	};

	self.redirectToDriveFileList = function () {
		const mobile = navigatorModel.isMobile() ? 'mobile/' : '';
		navigatorModel.redirect(`https://drive.google.com/drive/${mobile}search?q=type%3Aapplication%2Fvnd.mindmup`);
	};
	self.start = function () {
		startAuthenticator.backgroundAuthenticate(self.getLoginHint());
	};
	self.signIn = function () {
		startAuthenticator.changeAccount();
	};
	self.renameFile = function (newName) {
		const fileId = getFileId(getDriveState());
		newName = newName.trim();
		if (fileId && newName.length > 0) {
			googleDriveService.renameFile(fileId, newName).then(function () {
				self.dispatchEvent('renamed', newName);
			}, sendError);
		}
	};
	self.trashFile = function () {
		const fileId = getFileId(getDriveState());
		if (!fileId) {
			return;
		}
		self.dispatchEvent('trashing');
		googleDriveService.trash(fileId).then(function () {
			self.dispatchEvent('trashed');
		}, sendError);

	};
	self.trashImported = function () {
		if (importedFileId) {
			googleDriveService.trash(importedFileId);
		}
	};
	self.untrashFile = function () {
		const fileId = getFileId(getDriveState());
		if (!fileId) {
			return;
		}
		self.dispatchEvent('untrashing');
		googleDriveService.untrash(fileId).then(function () {
			self.dispatchEvent('untrashed');
		}, sendError);

	};
	self.overwriteFile = function () {
		return self.saveFile(true);
	};
	self.saveFile = function (force) {
		self.dispatchEvent('savingFile');
		return googleFileEditingSession.saveNewVersion({
			overwrite: force,
			progress: (arg) => self.dispatchEvent('uploadInProgress', arg)
		})
		.then(context => self.dispatchEvent('saveFileSuccess', context))
		.catch(e => {
			if (e === 'remote-changed') {
				self.dispatchEvent('saveConfirmationRequired');
			} else {
				self.dispatchEvent('error', e);
			}
		});
	};
	self.autosave = function () {
		self.dispatchEvent('autosavingFile');
		return googleFileEditingSession.saveNewVersion({
			progress: (arg) => self.dispatchEvent('uploadInProgress', arg)
		})
		.then(context => self.dispatchEvent('saveFileSuccess', context))
		.catch(e => {
			if (e === 'remote-changed') {
				self.dispatchEvent('autosaveConfirmationRequired');
			} else {
				self.dispatchEvent('error', e);
			}
		});

	};
	self.mergeAndSaveFile = function () {
		self.dispatchEvent('savingFile');
		return googleFileEditingSession.mergeFile()
			.then(context => self.dispatchEvent('fileMerged', context))
			.then(() => self.saveFile(true))
			.catch((e) => sendError(e));
	};
	self.mergeFile = function () {
		self.dispatchEvent('fileLoading');
		return googleFileEditingSession.mergeFile()
			.then(context => self.dispatchEvent('fileMerged', context))
			.catch((e) => sendError(e));
	};
	self.editFileId = function (fileId, requiredUserId) {
		navigatorModel.redirect('/editor-drive.html?state=' + encodeURIComponent(JSON.stringify({ids: [fileId], action: 'open', 'userId': requiredUserId || userId})));
	};
	self.requestSharingSettings = function (fileId) {
		self.dispatchEvent('shareSettingsRequested', fileId);
	};
	self.openReadOnly = function () {
		const fileId = getFileId(getDriveState());
		navigatorModel.locationReplace('/viewer.html?state=' + encodeURIComponent(JSON.stringify({ids: [fileId], action: 'open', 'userId': userId})));
	};
	self.confirmLoading = function () {
		const fileId = getFileId(getDriveState());
		return loadFile(fileId);
	};
	self.reloadEditor = function () {
		self.editFileId(getFileId(getDriveState()), userId);
	};
	self.renameFromDefault = (newTitle) => {
		if (!newTitle || newTitle === config.defaultFileTitle) {
			return Promise.resolve(false);
		}
		const fileId = getFileId(getDriveState());
		return googleDriveService.getMetaData(fileId)
		.then(fileMeta => {
			if (fileMeta && fileMeta.name === config.defaultFileTitle) {
				self.renameFile(newTitle);
			}
		});
	};
	self.turnOnAutosave = () => {
		return googleFileEditingSession.patchAppProperties({autosave: 'yes'})
		.then((context) => self.dispatchEvent('filePropertiesChanged', context))
		.then(() => self.mergeAndSaveFile());
	};
	self.turnOffAutosave = () => {
		return googleFileEditingSession.patchAppProperties({autosave: ''})
		.then((context) => self.dispatchEvent('filePropertiesChanged', context));
	};
	self.openMigrated = () => {
		const fileId = getFileId(getDriveState());
		return googleDriveService.getMetaData(fileId)
			.then(googleMetadataToFileContext)
			.then(context => {
				const currentMigrated = context && context.properties && context.properties.migrated,
					migratedComponents = currentMigrated.split(':');
				if (migratedComponents[0] === 'google') {
					self.editFileId(migratedComponents[1]);
				} else if (migratedComponents[0] === 'app') {
					navigatorModel.locationReplace(`${config.mindmupCloudUrl}/map/_v2/${migratedComponents[1]}`);
				}
			});

	};
};
